import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { useAuth } from '../../lib/auth-context';
import { authAPI } from '../../lib/api';
import { Shield, Key, Smartphone, AlertTriangle, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AccountSetting() {
  const { user, loadUser } = useAuth();
  // Email
  const [newEmail, setNewEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const handleEmailUpdate = async () => {
    if (!newEmail.trim() || !newEmail.includes('@')) { toast.error('Enter a valid email'); return; }
    setEmailSaving(true);
    try {
      await authAPI.updateEmail({ email: newEmail });
      await loadUser();
      toast.success('Email updated successfully');
      setNewEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setEmailSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!currentPassword) { toast.error('Enter your current password'); return; }
    if (newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setPwSaving(true);
    try {
      await authAPI.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Password updated successfully');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  const mfaEnabled = user?.mfa_enabled;

  return (
    <div className="space-y-6">
      {/* Email */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#1E5A8A]" /> Email Address
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-[#FBF7EF] border border-[#E7E0D6]">
            <div>
              <p className="text-sm font-medium">{user?.email || 'user@example.com'}</p>
              <p className="text-xs text-[#5B616E]">Primary email address</p>
            </div>
            <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50 text-xs">Active</Badge>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[#5B616E]">New Email Address</Label>
            <div className="flex gap-2">
              <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="newemail@example.com" className="flex-1" />
              <Button variant="outline" size="sm" onClick={handleEmailUpdate} disabled={emailSaving}>
                {emailSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Update'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Key className="w-4 h-4 text-[#B89B5E]" /> Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-[#5B616E]">Current Password</Label>
            <Input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter current password" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#5B616E]">New Password</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#5B616E]">Confirm New Password</Label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat new password" />
            </div>
          </div>
          {newPassword && confirmPassword && newPassword !== confirmPassword && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Passwords do not match
            </p>
          )}
          <div className="flex justify-end">
            <Button onClick={handlePasswordChange} disabled={pwSaving} className="bg-[#121316] text-white hover:bg-[#1B1D22]">
              {pwSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
              {pwSaving ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security / 2FA status */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#1F7A5A]" /> Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#1F7A5A]/10 flex items-center justify-center">
                <Smartphone className="w-4 h-4 text-[#1F7A5A]" />
              </div>
              <div>
                <Label className="text-sm">Two-Factor Authentication</Label>
                <p className="text-xs text-[#5B616E]">
                  {mfaEnabled
                    ? 'Your account is protected with 2FA via authenticator app'
                    : '2FA is mandatory for all staff — it will be set up at next login'}
                </p>
              </div>
            </div>
            <Badge variant="outline" className={mfaEnabled
              ? 'text-green-600 border-green-200 bg-green-50 text-xs'
              : 'text-amber-600 border-amber-200 bg-amber-50 text-xs'
            }>
              {mfaEnabled ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Enabled</> : 'Pending Setup'}
            </Badge>
          </div>
          <div className="h-px bg-[#E7E0D6]" />
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Account Role</Label>
              <p className="text-xs text-[#5B616E]">Your current role in the system</p>
            </div>
            <Badge variant="outline" className="text-[#B89B5E] border-[#B89B5E]/30 bg-[#B89B5E]/5 text-xs capitalize">
              {user?.role?.replace(/_/g, ' ') || 'Unknown'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="bg-white border-red-200">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Delete Account</p>
              <p className="text-xs text-[#5B616E]">Contact an administrator to delete your account</p>
            </div>
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" disabled>
              Contact Admin
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
